/**
 * Small pure helpers shared by the feature-capture workspace and the captured-flow
 * panels it renders. Kept in their own module so the panels can live outside the
 * workspace file without duplicating the display rules.
 */

// "assert" is the internal action name for a check recorded with "Add verification";
// the review screen shows testers the name they used.
const ACTION_DISPLAY_NAMES = {
  assert: 'Verification',
  capture_value: 'Saved value',
};

export const normalizeAction = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'Action';
  const named = ACTION_DISPLAY_NAMES[text.toLowerCase()];
  if (named) return named;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// What a recorded verification actually proves. A reviewer has to be able to tell a row
// that is merely present from one whose value was checked.
export const verificationSummary = (step) => {
  const assertion = step?.assertion;
  if (!assertion || typeof assertion !== 'object') return '';
  const withText = assertion.mode === 'visible_and_text';
  if (assertion.kind === 'table_row') return withText ? 'row + value' : 'row present';
  return withText ? 'visible + text' : 'visible';
};

export const formatCountLabel = (count, singular, plural = `${singular}s`) => {
  const value = Number(count || 0);
  return `${value} ${value === 1 ? singular : plural}`;
};

export const capturedFlowTabForRestore = (targetStage, targetTab = '') => {
  if (String(targetStage || '').trim() !== 'captured-flow') return '';
  return String(targetTab || '').trim() === 'components' ? 'components' : 'steps';
};

export const qualityForStep = (step) => {
  if (step?.action === 'navigate') {
    return { label: 'Page', className: 'bg-blue-50 text-blue-700' };
  }
  if (['tab_open', 'tab_switch', 'tab_close'].includes(String(step?.action || '').toLowerCase())) {
    return { label: 'Tab', className: 'bg-slate-100 text-slate-600' };
  }
  const score = Number(step?.locator_score || 0);
  if (score >= 80) return { label: 'High', className: 'bg-emerald-50 text-emerald-700' };
  if (score >= 50) return { label: 'Medium', className: 'bg-amber-50 text-amber-700' };
  return { label: step?.locator ? 'Review' : 'Missing', className: 'bg-rose-50 text-rose-700' };
};

export const stepSelectionId = (step, index) => String(step?.id || `step-${index + 1}`);

export const CONSUMABLE_STEP_ACTIONS = new Set(['fill', 'select', 'check', 'uncheck', 'assert']);

export const journeyVariableNamesById = (variables) => new Map(
  (Array.isArray(variables) ? variables : [])
    .filter((variable) => variable?.id)
    .map((variable) => [String(variable.id), String(variable.name || '')])
);

/**
 * How a step gets its value: it produces a runtime value, consumes one, or uses test
 * data. Shown next to the step so the link between two distant steps is visible.
 */
export const stepValueBinding = (step, namesById) => {
  const producedId = String(step?.captures_variable_id || '');
  if (producedId) {
    return { kind: 'produces', name: namesById.get(producedId) || 'unnamed value' };
  }
  const consumedId = String(step?.value_source?.variable_id || '');
  if (step?.value_source?.kind === 'variable' && consumedId) {
    return {
      kind: 'consumes',
      variableId: consumedId,
      name: namesById.get(consumedId) || '',
      missing: !namesById.has(consumedId),
    };
  }
  const suggestedId = String(step?.suggested_variable_id || '');
  if (suggestedId && namesById.has(suggestedId)) {
    return {
      kind: 'suggested',
      variableId: suggestedId,
      name: namesById.get(suggestedId),
      confidence: Number(step?.mapping_suggestion?.confidence || 0),
      reason: String(step?.mapping_suggestion?.reason || ''),
    };
  }
  return null;
};
